'use client';

import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'next/navigation';
import SearchModal from 'components/gruenland/atoms/search/SearchModal';
import BlockScroll from 'components/gruenland/atoms/utils/BlockScroll';
import { useCurrentCategory } from 'providers/category';
import HeaderLogo from './header-logo';
import HeaderNavigationDesktop from './header-navigation/header-navigation-desktop/HeaderNavigationDesktop';
import HeaderNavigationMobile from './header-navigation/header-navigation-mobile/HeaderNavigationMobile';
import { HeaderProps } from './types';
import UtilitySection from './utility-section/UtilitySection';

const Header: React.FC<HeaderProps> = ({
  cart,
  isEmpty,
  onRemoveDiscountCode,
  onApplyDiscountCode,
  totalCartItems,
  totalWishlistItems,
  onRemoveItem,
  onUpdateItem,
  wishlist,
  onMoveToCart,
  onRemoveFromWishlist,
  onClearWishlist,
  navLinks,
  categories,
  tiles,
  col1Label,
  col1Links,
  juniorLogoCategories,
}) => {
  const [searchVisible, setSearchVisible] = useState(false);

  const { slug } = useParams();
  const category = useMemo(
    () => categories.find((category) => slug && slug[0] && category.slug === slug[0]),
    [categories, slug],
  );

  const { categoryKeys } = useCurrentCategory();

  const toggleSearchVisible = useCallback(() => setSearchVisible((v) => !v), []);

  return (
    <header className="relative h-fit w-full border-b-[1.5px] border-neutral-400 bg-white">
      {searchVisible && <BlockScroll />}
      <div aria-label="Top" className="flex w-full items-center px-4 md:px-6 lg:px-5 xl:px-12">
        <div className="flex w-full justify-start py-[18px] md:w-fit lg:ml-5">
          <HeaderLogo
            variant={
              juniorLogoCategories?.map((x) => x.category).some((x) => new Set(categoryKeys).has(x)) ||
              (category?.categoryKey && juniorLogoCategories?.map((x) => x.category).includes(category.categoryKey))
                ? 'junior'
                : undefined
            }
          />
        </div>

        <div className="grow self-stretch">
          <HeaderNavigationDesktop links={navLinks} tiles={tiles ?? []} col1Label={col1Label} col1Links={col1Links} />
        </div>

        <div className="flex items-center gap-x-2 md:gap-x-4">
          <UtilitySection
            cart={cart}
            onRemoveDiscountCode={onRemoveDiscountCode}
            onApplyDiscountCode={onApplyDiscountCode}
            isEmpty={isEmpty}
            onRemoveItem={onRemoveItem}
            onUpdateItem={onUpdateItem}
            wishlist={wishlist}
            onRemoveFromWishlist={onRemoveFromWishlist}
            onMoveToCart={onMoveToCart}
            onClearWishlist={onClearWishlist}
            totalCartItems={totalCartItems}
            totalWishlistItems={totalWishlistItems}
            onSearchClick={toggleSearchVisible}
          />

          <HeaderNavigationMobile links={navLinks} tiles={tiles ?? []} onSearchClick={toggleSearchVisible} />
        </div>
      </div>

      {searchVisible && <SearchModal onClose={toggleSearchVisible} />}
    </header>
  );
};
export default Header;
